import { ref, computed, watch } from 'vue';
import axios from 'axios';
import utils from '@/common/utils.js';

const MAX_SELECT_ITEMS = 10;
const FILTERS_STORAGE_PREFIX = 'levelling-filters-v1';

export function useLevellingData({ store, loginState, presetFilters = null } = {}) {
  const self = {
    // Filter state
    filterText: ref(''),
    hierarchyFieldFilters: ref({}),
    classifierFieldFilters: ref({}),
    docCreatedDateRange: ref([]),
    hierarchyFilters: ref([]),
    classifierFilters: ref([]),
    extraColumnFilters: ref({}),

    // Table data
    items: ref([]),
    filteredItems: ref([]),
    totalItemCount: ref(0),
    currentPage: ref(1),
    rowsPerPage: ref(20),
    sortColumn: ref('system_number'),
    sortDesc: ref(true),
    searchInProgress: ref(false),
    docStatusIncluded: ref([]),
    docTypeIncludedLocal: ref([]),
    evaluationStatusMap: ref({}),
    levellingCounts: ref(null),
  };

  let _persistEnabled = false;

  function _storageKey() {
    const uid = loginState?.user?.user_id || 'default';
    return `${FILTERS_STORAGE_PREFIX}:${uid}`;
  }

  function _persistFilters() {
    if (!_persistEnabled) return;
    try {
      const state = {
        filterText: self.filterText.value,
        hierarchyFieldFilters: self.hierarchyFieldFilters.value,
        classifierFieldFilters: self.classifierFieldFilters.value,
        docCreatedDateRange: self.docCreatedDateRange.value,
        hierarchyFilters: self.hierarchyFilters.value,
        classifierFilters: self.classifierFilters.value,
        extraColumnFilters: self.extraColumnFilters.value,
        docStatusKeys: (self.docStatusIncluded.value || []).map(s => s.status),
        docTypeKeys: (self.docTypeIncludedLocal.value || []).map(t => t.docType),
      };
      localStorage.setItem(_storageKey(), JSON.stringify(state));
    } catch (err) {
      console.warn('Failed to persist levelling filters:', err);
    }
  }

  function _readPersistedFilters() {
    try {
      const raw = localStorage.getItem(_storageKey());
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn('Failed to read levelling filters:', err);
      return null;
    }
  }

  watch(
    [
      self.filterText,
      self.hierarchyFieldFilters,
      self.classifierFieldFilters,
      self.docCreatedDateRange,
      self.hierarchyFilters,
      self.classifierFilters,
      self.extraColumnFilters,
      self.docStatusIncluded,
      self.docTypeIncludedLocal,
    ],
    () => _persistFilters(),
    { deep: true }
  );

  // Layer any per-doc updates from the store (dispatched via docs/updateDoc,
  // e.g. after an evaluation completes) on top of the current page. Used both
  // after a server fetch and reactively while the view is mounted.
  function _applyDocsOverlay() {
    const overlay = store.state.docs.docsList;
    if (!overlay || !overlay.length || !self.filteredItems.value?.length) return;
    let changed = false;
    const next = self.filteredItems.value.map(item => {
      const fresh = overlay.find(d => d.doc_id === item.doc_id);
      if (fresh) {
        changed = true;
        return { ...item, ...fresh };
      }
      return item;
    });
    if (changed) self.filteredItems.value = next;
  }

  watch(() => store.state.docs.listUpdated, _applyDocsOverlay);

  // --- Store accessors ---
  function _storeState() {
    return store.state;
  }

  self.isLoading = computed(() => _storeState().docs.loading);
  self.useIndex = computed(() => _storeState().docs.useIndex);
  self.cleanItems = computed(() => _storeState().docs.docsList);
  self.hierarchyTypes = computed(() => _storeState().docs.hierarchyTypes);
  self.classifierTypes = computed(() => _storeState().docs.classifierTypes);
  self.docStatusSummary = computed(() => _storeState().docs.docStatusSummary);
  self.docTypeSummary = computed(() => _storeState().docs.docTypeSummary);

  // Templates whose workflow contains an "Evaluate" step (a LEVELLING data capture).
  self.levellingTemplates = computed(() =>
    (loginState?.user?.createDocumentDialogOptions || []).filter(dt =>
      dt.lifecycles.some(l => l.dataCaptures.some(dc => dc.questionnaire_control_type === 'LEVELLING'))
    )
  );

  self.levellingDocTypeNames = computed(() => {
    const names = new Set(self.levellingTemplates.value.map(dt => dt.tmpl_name));
    (self.evaluatorRoles.value || []).forEach(r => { if (r.tmpl_name) names.add(r.tmpl_name); });
    return names;
  });

  // Restrict the levelling doc-type filter to types that actually have an evaluate
  // workflow (and never Employee).
  self.docTypeSummaryFiltered = computed(() =>
    (self.docTypeSummary.value || []).filter(dt =>
      dt.docType !== 'Employee' && self.levellingDocTypeNames.value.has(dt.docType)
    )
  );

  self.defaultDocTypeKeys = computed(() => {
    const summary = self.docTypeSummaryFiltered.value || [];
    const jp = summary.find(t => t.docType === 'Job Profile 2.0');
    return (jp ? [jp] : summary).map(t => t.docType);
  });
  self.isDocTypeDefault = computed(() => {
    const cur = (self.docTypeIncludedLocal.value || []).map(t => t.docType).slice().sort();
    const def = self.defaultDocTypeKeys.value.slice().sort();
    return cur.length === def.length && cur.every((k, i) => k === def[i]);
  });

  // Evaluator role(s) resolved at levelling-page load via the isolated
  // Report/levellingEvaluatorRoles endpoint. One entry per template:
  // { tmpl_id, tmpl_name, role_name, doclist_column }. Different doc types may map to
  // different evaluator roles, so this is resolved per row at render time.
  self.evaluatorRoles = ref([]);

  // Map of doc type (tmpl_name) -> evaluator person-column field.
  self.evaluatorFieldByDocType = computed(() => {
    const map = {};
    for (const r of self.evaluatorRoles.value) {
      if (r.tmpl_name && r.doclist_column) map[r.tmpl_name] = r.doclist_column;
    }
    return map;
  });

  // Representative role used for the single "Evaluator" column's identity (header, sort,
  // persistence) and as a fallback for doc types not present in the map. Falls back to
  // "Contributor" (the historical hardcoded role) until the lookup resolves.
  self.evaluatorRoleName = computed(() =>
    self.evaluatorRoles.value[0]?.role_name || 'Contributor'
  );

  // The column field for the representative role. The backend supplies the field directly;
  // derive it locally only for the fallback (mirrors the backend convention, note the
  // load-bearing 'lifecyle' typo).
  self.evaluatorColumnField = computed(() =>
    self.evaluatorRoles.value[0]?.doclist_column ||
    'lifecyle_role_' + self.evaluatorRoleName.value.toLowerCase().replace(/ /g, '_')
  );

  // Resolve the evaluator display value for a given row based on its doc type, falling
  // back to the representative role's field when the doc type isn't mapped.
  self.evaluatorValueForItem = (item) => {
    if (!item) return '';
    const field = self.evaluatorFieldByDocType.value[item.doc_type] || self.evaluatorColumnField.value;
    return item[field] || '';
  };
  self.config = computed(() => _storeState().settings.config);
  self.hierarchyLookup = computed(() => _storeState().hierarchies.hierarchyLookup);
  self.docClassifiers = computed(() => _storeState().hierarchies.docClassifiers);

  // --- Computed ---
  self.hierarchyLevelColumns = computed(() => {
    const cols = [];
    (self.hierarchyTypes.value || []).forEach(ht => {
      const usedLevels = [];
      for (let i = 1; i <= 6; i++) {
        if (ht[`use_h${i}`]) {
          usedLevels.push({ name: ht[`level${i}_name`] || `Level ${i}` });
        }
      }
      const totalLevels = usedLevels.length;
      for (let i = 0; i < totalLevels - 1; i++) {
        const hryLevel = totalLevels - i;
        cols.push({
          field: 'hry' + ht.position + '_' + hryLevel,
          label: usedLevels[i].name,
          position: ht.position,
          level: hryLevel,
        });
      }
    });
    return cols;
  });

  self.totalPages = computed(() => {
    return Math.ceil(self.totalItemCount.value / self.rowsPerPage.value) || 1;
  });

  self.currentPageItems = computed(() => {
    if (self.useIndex.value) return self.filteredItems.value;
    const start = (self.currentPage.value - 1) * self.rowsPerPage.value;
    return self.filteredItems.value.slice(start, start + self.rowsPerPage.value);
  });

  // Unified counts: prefer server-aggregated values (index mode); otherwise
  // tally over the full client-filtered list.
  function _evalStatusFor(d) {
    return self.evaluationStatusMap.value[d.doc_id]?.evaluation_status ?? d.evaluation_status;
  }

  self.totalRolesCount = computed(() => {
    if (self.levellingCounts.value) return self.levellingCounts.value.total;
    return self.totalItemCount.value || (self.filteredItems.value || []).length;
  });

  // Counts track the OFFICIAL levelling process (document_lf_evaluation status),
  // NOT whether a job-level field is populated. Server (index) values come from
  // Report/levellingCounts; the non-index fallback tallies the same way over the
  // filtered list using each row's latest evaluation status.
  self.rolesLevelledCount = computed(() => {
    if (self.levellingCounts.value) return self.levellingCounts.value.levelled;
    return (self.filteredItems.value || []).filter(d => _evalStatusFor(d) === 'COMPLETED').length;
  });

  self.rolesInProgressCount = computed(() => {
    if (self.levellingCounts.value) return self.levellingCounts.value.inProgress;
    return (self.filteredItems.value || []).filter(d => _evalStatusFor(d) === 'DRAFT').length;
  });

  self.rolesToBeLevelledCount = computed(() => {
    if (self.levellingCounts.value) return self.levellingCounts.value.toBeLevelled;
    return (self.filteredItems.value || [])
      .filter(d => { const s = _evalStatusFor(d); return s !== 'COMPLETED' && s !== 'DRAFT'; }).length;
  });

  // Field filters accept EITHER a single value (legacy single-select, e.g. the
  // old levelling view) OR an array (multi-select, the control centre). These
  // helpers normalise both: `_hasVal` = is the filter active, `_valMatches` = does
  // a cell value satisfy it (exact for single; membership for multi/array).
  function _hasVal(v) {
    return Array.isArray(v) ? v.length > 0 : (v != null && v !== '');
  }
  function _valMatches(cellVal, filterVal) {
    return Array.isArray(filterVal) ? filterVal.includes(cellVal) : cellVal === filterVal;
  }

  self.activeFilterCount = computed(() => {
    let count = 0;
    Object.values(self.hierarchyFieldFilters.value).forEach(v => { if (_hasVal(v)) count++; });
    Object.values(self.classifierFieldFilters.value).forEach(v => { if (_hasVal(v)) count++; });
    Object.values(self.extraColumnFilters.value).forEach(v => { if (_hasVal(v)) count++; });
    if (self.docCreatedDateRange.value.length) count++;
    if (self.filterText.value) count++;
    // Doc type only counts when the user has narrowed away from the default scope.
    if (!self.isDocTypeDefault.value) count++;
    return count;
  });

  // --- Private helpers ---
  function _hasExploreSelections() {
    const hasHry = self.hierarchyFilters.value && self.hierarchyFilters.value.some(hf => hf.selected && hf.selected.length);
    const hasCls = self.classifierFilters.value && self.classifierFilters.value.some(cf => cf.selected && cf.selected.length);
    return hasHry || hasCls;
  }

  // `exclude` (optional {pos, lvl}) drops the given level AND any deeper level
  // (lower lvl number) at the same position from the filter set — so a dropdown's
  // OWN selection (and its descendants') never trims its own option list, while
  // ANCESTOR selections still cascade down (prototype: group → family).
  function _getFilteredLookupEntries(exclude) {
    const activeFilters = [];
    Object.entries(self.hierarchyFieldFilters.value).forEach(([field, val]) => {
      if (!_hasVal(val)) return;
      const m = field.match(/^hry(\d+)_(\d+)$/);
      if (!m) return;
      const pos = Number(m[1]);
      const lvl = Number(m[2]);
      if (exclude && pos === exclude.pos && lvl <= exclude.lvl) return;
      activeFilters.push({ pos, lvl, val });
    });

    const treeAllowedByPos = {};
    if (self.hierarchyFilters.value && self.hierarchyFilters.value.length) {
      self.hierarchyFilters.value.forEach(hf => {
        if (!hf.selected || !hf.selected.length) return;
        const ht = (self.hierarchyTypes.value || []).find(h => h.ht_id === hf.ht_id);
        if (!ht) return;
        const ids = new Set();
        hf.selected.forEach(s => {
          const raw = String(s.id);
          const hrId = raw.includes('_') ? Number(raw.split('_')[0]) : Number(raw);
          if (!isNaN(hrId)) ids.add(hrId);
        });
        if (ids.size) treeAllowedByPos[ht.position] = ids;
      });
    }

    const hasDropdownFilters = activeFilters.length > 0;
    const hasTreeFilters = Object.keys(treeAllowedByPos).length > 0;
    if (!hasDropdownFilters && !hasTreeFilters) return Object.values(self.hierarchyLookup.value);

    return Object.values(self.hierarchyLookup.value).filter(h => {
      if (hasDropdownFilters) {
        const passesDropdowns = activeFilters.every(f =>
          f.pos !== h.position || _valMatches(h[`hierarchy${f.lvl}`], f.val)
        );
        if (!passesDropdowns) return false;
      }
      if (hasTreeFilters && treeAllowedByPos[h.position]) {
        if (!treeAllowedByPos[h.position].has(h.hr_id)) return false;
      }
      return true;
    });
  }

  function _buildHierarchyFiltersFromDropdowns() {
    const activeDropdowns = {};
    Object.entries(self.hierarchyFieldFilters.value).forEach(([field, val]) => {
      if (!_hasVal(val)) return;
      const m = field.match(/^hry(\d+)_(\d+)$/);
      if (m) {
        const pos = Number(m[1]);
        const lvl = Number(m[2]);
        if (!activeDropdowns[pos]) activeDropdowns[pos] = [];
        activeDropdowns[pos].push({ lvl, val });
      }
    });

    const filters = [];
    (self.hierarchyTypes.value || []).forEach(ht => {
      const dropdowns = activeDropdowns[ht.position];
      const treeFilter = (self.hierarchyFilters.value || []).find(f => f.ht_id === ht.ht_id);
      if (!dropdowns && !treeFilter) return;

      let matchingIds;
      if (dropdowns) {
        matchingIds = [];
        Object.values(self.hierarchyLookup.value).forEach(h => {
          if (h.position !== ht.position) return;
          const matches = dropdowns.every(d => _valMatches(h[`hierarchy${d.lvl}`], d.val));
          if (matches) matchingIds.push({ id: h.hr_id });
        });
      }

      if (treeFilter && treeFilter.selected && treeFilter.selected.length) {
        if (matchingIds) {
          const treeIds = new Set(treeFilter.selected.map(s => Number(String(s.id).split('_')[0])));
          matchingIds = matchingIds.filter(m => treeIds.has(m.id));
        } else {
          matchingIds = treeFilter.selected.map(s => ({ id: Number(String(s.id).split('_')[0]) }));
        }
      }

      if (matchingIds && matchingIds.length) {
        filters.push({ ht_id: ht.ht_id, selected: matchingIds });
      }
    });
    return filters;
  }

  function _buildClassifierFiltersFromDropdowns() {
    const filters = [];
    (self.docClassifiers.value || []).forEach(ct => {
      const selectedText = self.classifierFieldFilters.value['classifier_' + ct.ct_id];
      const treeFilter = (self.classifierFilters.value || []).find(f => f.ct_id === ct.ct_id);

      let matchingIds;
      if (selectedText) {
        const cv = (ct.values || []).find(v => v.value === selectedText);
        if (cv) matchingIds = [{ cv_id: cv.cv_id }];
      }

      if (treeFilter && treeFilter.selected && treeFilter.selected.length) {
        if (matchingIds) {
          const treeIds = new Set(treeFilter.selected.map(s => Number(s.cv_id)));
          matchingIds = matchingIds.filter(m => treeIds.has(m.cv_id));
        } else {
          matchingIds = treeFilter.selected.map(s => ({ cv_id: Number(s.cv_id) }));
        }
      }

      if (matchingIds && matchingIds.length) {
        filters.push({ ct_id: ct.ct_id, selected: matchingIds });
      }
    });
    return filters;
  }

  function _applyDateFilter(docs) {
    if (!self.docCreatedDateRange.value || !self.docCreatedDateRange.value.length) return docs;
    const sorted = [...self.docCreatedDateRange.value].sort();
    const from = sorted[0];
    const to = sorted.length > 1 ? sorted[1] : from;
    return docs.filter(d => {
      const created = (d.created_date || d.job_created_at || '').substring(0, 10);
      return created >= from && created <= to;
    });
  }

  function _applyClientFilters() {
    let result = [...self.items.value];

    Object.entries(self.hierarchyFieldFilters.value).forEach(([field, val]) => {
      if (_hasVal(val)) {
        result = result.filter(i => _valMatches(utils.removeTags(i[field] || ''), val));
      }
    });

    Object.entries(self.classifierFieldFilters.value).forEach(([field, val]) => {
      if (_hasVal(val)) {
        result = result.filter(i => _valMatches(utils.removeTags(i[field] || ''), val));
      }
    });

    if (self.hierarchyFilters.value) {
      self.hierarchyFilters.value.forEach(hf => {
        if (hf && hf.selected && hf.selected.length) {
          result = result.filter(d =>
            hf.selected.find(s => {
              const ht = (d.hierarchies || []).find(h => h.ht_id === hf.ht_id);
              return ht && s.id.split('_')[0] == ht.hr_id;
            })
          );
        }
      });
    }

    if (self.classifierFilters.value) {
      self.classifierFilters.value.forEach(cf => {
        if (cf && cf.selected && cf.selected.length) {
          const cvs = cf.selected.map(c => c.cv_id);
          result = result.filter(d =>
            (d.classifiers || []).some(c => cvs.includes(c.cv_id)) ||
            (cvs.includes(0) && !(d.classifiers || []).some(c => c.ct_id === cf.ct_id))
          );
        }
      });
    }

    result = _applyDateFilter(result);

    Object.entries(self.extraColumnFilters.value).forEach(([field, val]) => {
      if (val) {
        if (self.isSelectFilter(field)) {
          result = result.filter(i => utils.removeTags(i[field] || '') === val);
        } else {
          const search = val.toLowerCase();
          result = result.filter(i => utils.removeTags(i[field] || '').toLowerCase().includes(search));
        }
      }
    });

    self.filteredItems.value = result;
    self.totalItemCount.value = result.length;
  }

  // --- Public methods ---
  self.buildFilters = (includeHierarchy) => {
    const params = {
      filterText: (self.filterText.value || '').trim().toLowerCase(),
      myJobs: false,
      docCreatedDateRange: self.docCreatedDateRange.value,
      docStatusIncluded: self.docStatusIncluded.value,
      docScoresIncluded: [],
      docTypeIncluded: self.docTypeIncludedLocal.value,
      peopleIncluded: [],
      reviewGroupsIncluded: [],
      fieldFilters: { ...self.extraColumnFilters.value },
      sortColumn: self.sortColumn.value,
      sortDesc: self.sortDesc.value,
      currentPage: self.currentPage.value,
      pageSize: self.rowsPerPage.value,
      useOpenSearch: false,
    };
    if (includeHierarchy) {
      params.hierarchyFilters = _buildHierarchyFiltersFromDropdowns();
      params.classifierFilters = _buildClassifierFiltersFromDropdowns();
    }
    return params;
  };

  self.getDistinctValues = (field) => {
    const vals = new Set();

    if (_hasExploreSelections()) {
      const source = self.useIndex.value ? self.filteredItems.value : (self.cleanItems.value.length ? self.cleanItems.value : self.items.value);
      (source || []).forEach(item => {
        const v = item[field];
        if (v) vals.add(typeof v === 'string' ? utils.removeTags(v) : v);
      });
      return Array.from(vals).sort();
    }

    const hryMatch = field.match(/^hry(\d+)_(\d+)$/);
    if (hryMatch && self.hierarchyLookup.value) {
      const pos = Number(hryMatch[1]);
      const lvl = Number(hryMatch[2]);
      _getFilteredLookupEntries({ pos, lvl }).forEach(h => {
        if (h.position === pos && h[`hierarchy${lvl}`]) {
          vals.add(h[`hierarchy${lvl}`]);
        }
      });
      return Array.from(vals).sort();
    }

    const clsMatch = field.match(/^classifier_(\d+)$/);
    if (clsMatch && self.docClassifiers.value) {
      const ctId = Number(clsMatch[1]);
      const ct = self.docClassifiers.value.find(c => c.ct_id === ctId);
      if (ct && ct.values) {
        ct.values.forEach(v => { if (v.value) vals.add(v.value); });
      }
      return Array.from(vals).sort();
    }

    if (field === 'doc_type' && self.docTypeSummaryFiltered.value && self.docTypeSummaryFiltered.value.length) {
      self.docTypeSummaryFiltered.value.forEach(dt => { if (dt.docType) vals.add(dt.docType); });
      return Array.from(vals).sort();
    }
    if (field === 'doc_status_text' && self.docStatusSummary.value && self.docStatusSummary.value.length) {
      self.docStatusSummary.value.forEach(ds => { if (ds.status) vals.add(ds.status); });
      return Array.from(vals).sort();
    }

    const source = self.cleanItems.value.length ? self.cleanItems.value : self.items.value;
    (source || []).forEach(item => {
      const v = item[field];
      if (v) vals.add(typeof v === 'string' ? utils.removeTags(v) : v);
    });
    return Array.from(vals).sort();
  };

  self.isSelectFilter = (field) => {
    const distinct = self.getDistinctValues(field);
    return distinct.length > 0 && distinct.length <= MAX_SELECT_ITEMS;
  };

  self.setFieldFilter = (filterObj, key, val) => {
    self[filterObj].value = { ...self[filterObj].value, [key]: val };
    self.doFilter();
  };

  self.setExtraColumnFilter = (key, val) => {
    self.extraColumnFilters.value = { ...self.extraColumnFilters.value, [key]: val };
  };

  self.removeExtraColumnFilter = (key) => {
    const copy = { ...self.extraColumnFilters.value };
    delete copy[key];
    self.extraColumnFilters.value = copy;
  };

  self.doFilter = async () => {
    self.searchInProgress.value = true;
    try {
      const filters = self.buildFilters(true);
      if (self.useIndex.value) {
        const result = await store.getters['docs/pagedFilter'](filters);
        self.filteredItems.value = _applyDateFilter(result.documents);
        self.totalItemCount.value = result.totalCount;
      } else {
        const result = await store.getters['docs/filter'](filters);
        self.items.value = result;
        _applyClientFilters();
      }
      _applyDocsOverlay();
    } finally {
      self.searchInProgress.value = false;
    }
    await self.fetchEvaluationStatus();
    await self.fetchLevellingCounts();
  };

  self.fetchLevellingCounts = async () => {
    if (!self.useIndex.value) {
      // Non-index mode tallies from filteredItems client-side; no server call needed.
      self.levellingCounts.value = null;
      return;
    }
    try {
      const filters = self.buildFilters(true);
      const resp = await axios.post('Report/levellingCounts', filters);
      if (resp?.data?.Status === 'OK' && resp.data.Data) {
        self.levellingCounts.value = resp.data.Data;
      }
    } catch (err) {
      console.error('Failed to fetch levelling counts:', err);
      self.levellingCounts.value = null;
    }
  };

  self.fetchEvaluatorRoles = async () => {
    try {
      const resp = await axios.get('Report/levellingEvaluatorRoles');
      self.evaluatorRoles.value = Array.isArray(resp?.data?.Data) ? resp.data.Data : [];
    } catch (err) {
      console.error('Failed to fetch levelling evaluator roles:', err);
      self.evaluatorRoles.value = [];
    }
  };

  self.fetchEvaluationStatus = async () => {
    const sources = [
      self.filteredItems.value,
      self.items.value,
      _storeState().docs.docsList,
    ];
    const seen = new Set();
    for (const list of sources) {
      if (!Array.isArray(list)) continue;
      for (const d of list) {
        if (d?.doc_id) seen.add(d.doc_id);
      }
    }
    if (!seen.size) {
      self.evaluationStatusMap.value = {};
      return;
    }
    try {
      const resp = await axios.post('Report/evaluationStatus', {
        docIds: [...seen],
      });
      const data = resp?.data?.Data;
      const next = {};
      if (Array.isArray(data)) {
        for (const row of data) {
          if (row?.doc_id) next[row.doc_id] = row;
        }
      }
      self.evaluationStatusMap.value = next;
    } catch (err) {
      console.error('Failed to fetch evaluation status:', err);
      self.evaluationStatusMap.value = {};
    }
  };

  self.getEvaluationStatus = (docId) => {
    if (!docId) return {};
    return self.evaluationStatusMap.value[docId] || {};
  };

  function _applyDefaultStatuses() {
    self.docStatusIncluded.value = [...self.docStatusSummary.value.filter(s => s.show)];
    /*const defaultStatuses = utils.getDefaultStatuses(loginState);
    const checkDefaultStatusesExist = defaultStatuses.some(ds =>
      self.docStatusSummary.value.some(s => s.doc_status === ds.doc_status)
    );
    if (defaultStatuses.length && checkDefaultStatusesExist) {
      self.docStatusIncluded.value = defaultStatuses;
    } else {
      self.docStatusIncluded.value = [...self.docStatusSummary.value.filter(s => s.show)];
    }*/
  }

  // Map status / doc-type NAME strings (as handed over by a deep-link, e.g. a
  // homepage Stats tile via $passedParams) to the matching summary entries the
  // filters expect. Returns true if at least one matched (so callers can fall
  // back to defaults when a preset doesn't apply to this catalogue).
  function _applyStatusNames(names) {
    const summary = self.docStatusSummary.value || [];
    const picked = (names || []).map(k => summary.find(s => s.status === k)).filter(Boolean);
    if (picked.length) { self.docStatusIncluded.value = picked; return true; }
    return false;
  }
  function _applyDocTypeNames(names) {
    // Restricted to levelling doc types (docTypeSummaryFiltered) — a non-levelling
    // type passed in simply won't match and we fall back to the default.
    const summary = self.docTypeSummaryFiltered.value || [];
    const picked = (names || []).map(k => summary.find(t => t.docType === k)).filter(Boolean);
    if (picked.length) { self.docTypeIncludedLocal.value = picked; return true; }
    return false;
  }

  function _applyDefaultDocType() {
    // docTypeSummaryFiltered is already restricted to types with an evaluate workflow.
    _applyDocTypeNames(self.defaultDocTypeKeys.value);
  }

  self.setDocTypeKeys = (names) => {
    if (!names || !names.length) _applyDefaultDocType();
    else _applyDocTypeNames(names);
    self.doFilter();
  };

  self.fetchData = async () => {
    self.fetchEvaluatorRoles();

    // A deep-link preset (status/doc-type names from $passedParams) takes
    // precedence: when present we start from a clean slate (ignore persisted
    // filters) so the view matches exactly what the linking tile asked for.
    const preset = presetFilters || {};
    const presetStatuses = Array.isArray(preset.statuses) ? preset.statuses.filter(Boolean) : [];
    const presetDocTypes = Array.isArray(preset.docTypes) ? preset.docTypes.filter(Boolean) : [];
    const hasPreset = presetStatuses.length > 0 || presetDocTypes.length > 0;

    const saved = hasPreset ? null : _readPersistedFilters();
    if (saved) {
      if ('filterText' in saved) self.filterText.value = saved.filterText || '';
      if (saved.hierarchyFieldFilters) self.hierarchyFieldFilters.value = saved.hierarchyFieldFilters;
      if (saved.classifierFieldFilters) self.classifierFieldFilters.value = saved.classifierFieldFilters;
      if (saved.docCreatedDateRange) self.docCreatedDateRange.value = saved.docCreatedDateRange;
      if (saved.hierarchyFilters) self.hierarchyFilters.value = saved.hierarchyFilters;
      if (saved.classifierFilters) self.classifierFilters.value = saved.classifierFilters;
      if (saved.extraColumnFilters) self.extraColumnFilters.value = saved.extraColumnFilters;

      if (Array.isArray(saved.docStatusKeys) && saved.docStatusKeys.length) {
        const summary = self.docStatusSummary.value || [];
        self.docStatusIncluded.value = saved.docStatusKeys
          .map(k => summary.find(s => s.status === k))
          .filter(Boolean);
      } else {
        _applyDefaultStatuses();
      }

      if (Array.isArray(saved.docTypeKeys) && saved.docTypeKeys.length) {
        const summary = self.docTypeSummaryFiltered.value || [];
        self.docTypeIncludedLocal.value = saved.docTypeKeys
          .map(k => summary.find(t => t.docType === k))
          .filter(Boolean);
      } else {
        _applyDefaultDocType();
      }
    } else {
      // Preset (if any) wins; otherwise defaults. Each dimension is independent,
      // so a tile that presets only a status still gets the default doc type.
      if (!(presetStatuses.length && _applyStatusNames(presetStatuses))) _applyDefaultStatuses();
      if (!(presetDocTypes.length && _applyDocTypeNames(presetDocTypes))) _applyDefaultDocType();
    }

    _persistEnabled = true;
    await self.doFilter();
  };

  self.clearAllFilters = () => {
    self.filterText.value = '';
    self.hierarchyFieldFilters.value = {};
    self.classifierFieldFilters.value = {};
    self.extraColumnFilters.value = {};
    self.docCreatedDateRange.value = [];
    _applyDefaultDocType();
    self.doFilter();
  };

  self.onHierarchyFilterChanged = (hierarchies, classifiers) => {
    self.hierarchyFilters.value = hierarchies || [];
    self.classifierFilters.value = classifiers || [];
    self.doFilter();
  };

  self.onPageChange = () => {
    if (self.useIndex.value) self.doFilter();
  };

  let _extraTextTimer = null;
  self.onExtraTextInput = (field, val) => {
    self.setExtraColumnFilter(field, val);
    if (_extraTextTimer) clearTimeout(_extraTextTimer);
    _extraTextTimer = setTimeout(() => { self.doFilter(); }, 500);
  };

  return self;
}
